/*
 * Copyright (c) 2024-2026 Nodals AI Ltd
 * All rights reserved.
 */

import { Opportunity } from '../../auction';
import { INVALID_CLICK_WINDOW_MS } from '../../activity/constants';
import { ActivityManager } from '../../activity/manager';
import { EventDispatcher } from '../../events';
import { Logger } from '../../logger';
import { CampaignType, type CampaignData } from '../../types';
import type { AdvertorialConfig, DiscoveredUnit } from '../../types/advertorial';
import type { PlacementType, Visibility } from '../placement/constants';
import { generateUUID } from '../../util';
import { AdvertorialConfigValidator } from './config';
import { AdvertorialDiscovery } from './discovery';
import { MetadataExtractor } from './extractor';
import { buildAdvertorialMatchUrlMap, normaliseAdvertorialMatchUrl } from './match-url';
import { AdvertorialAttribution, AdvertorialPlacement } from './placement';

export { buildAdvertorialMatchUrlMap, normaliseAdvertorialMatchUrl } from './match-url';

interface AdvertorialTrackerEvents {
  AdvertorialUnitsDiscovered: { opportunityId: string; placements: AdvertorialPlacement[] };
  AdvertorialAccidentalClick: {
    opportunityId: string;
    placementId: string;
    id: string;
    deliveryId: string;
    awayDuration: number;
    source: 'redirect';
    t: number;
  };
  AdvertorialImpression: {
    opportunityId: string;
    placementId: string;
    id: string;
    deliveryId: string;
    meta?: Record<string, string>;
    t: number;
    v: Visibility;
    pt: PlacementType;
  };
  AdvertorialInteraction: {
    opportunityId: string;
    placementId: string;
    id: string;
    deliveryId: string;
    clickUrl?: string;
    t: number;
  };
  AdvertorialViewableImpression: {
    impressionId: string;
    deliveryId: string;
    opportunityId: string;
    placementId: string;
    t: number;
  };
}

const logger = Logger.getInstance();

// Advertorial events use t=1; reconciled redirect clicks inherit the same value.
const ADVERTORIAL_RECONCILE_T_VALUE = 1;

/**
 * Main orchestrator for advertorial tracking
 * Coordinates discovery, extraction, and event generation
 */
export class AdvertorialTracker extends EventDispatcher<AdvertorialTrackerEvents> {
  private discovery!: AdvertorialDiscovery;
  private extractor!: MetadataExtractor;
  private configValidator: AdvertorialConfigValidator;
  private discoveredUnits: Map<string, AdvertorialPlacement> = new Map();
  private attribution: AdvertorialAttribution;
  // Bound once so add/removeEventListener reference the identical handler.
  // Runs inside a window 'pageshow' handler and dispatches into analytics, so a
  // downstream throw would escape uncaught onto the publisher page — contain it.
  private readonly pageshowHandler = (): void => {
    try {
      this.reconcilePendingAccidentalClick();
    } catch (e) {
      logger.error(`Accidental-click reconcile failed: ${e}`);
    }
  };

  constructor(config: AdvertorialConfig, campaignData: CampaignData[]) {
    super();
    const learningDirectCampaign = campaignData.find(
      (campaign) => campaign.type === CampaignType.LEARNING_DIRECT,
    );
    const defaultDeliveryId =
      learningDirectCampaign && learningDirectCampaign.ads.length > 0
        ? learningDirectCampaign.ads[0].id
        : undefined;
    this.attribution = {
      defaultDeliveryId,
      urlMap: buildAdvertorialMatchUrlMap(campaignData),
    };
    this.configValidator = new AdvertorialConfigValidator(config);

    logger.info(
      `Initialising advertorial tracking - timing: ${this.configValidator.getDiscoveryConfig().timing}, selectors: ${JSON.stringify(this.configValidator.getDiscoveryConfig().selectors)}`,
    );

    this.discovery = new AdvertorialDiscovery(
      this.configValidator.getDiscoveryConfig(),
      this.configValidator.getPlacementIdConfig(),
    );

    this.extractor = new MetadataExtractor(this.configValidator.getExtractionConfig());
  }

  /**
   * Start advertorial tracking
   * Begins discovery based on configured timing
   */
  public start(): void {
    logger.info('Starting advertorial discovery');

    // Reconcile a same-tab redirect accidental click on return. pageshow fires
    // on both a full reload AND a bfcache restore (back-button), the latter of
    // which runs no discovery — so this listener, not discovery, drives
    // reconciliation. It also fires on the initial load, where it safely no-ops
    // (no pending flag unless an advertorial click occurred previously).
    window.addEventListener('pageshow', this.pageshowHandler);

    this.discovery.start((units) => {
      this.handleDiscoveredUnits(units);
    });
  }

  /**
   * Handle discovered advertorial units
   */
  private handleDiscoveredUnits(units: DiscoveredUnit[]): void {
    if (units.length === 0) {
      logger.info('No advertorial units discovered');
      return;
    }

    logger.info(`Discovered ${units.length} advertorial units`);

    const opportunity = new Opportunity(generateUUID());
    const deferUntilContent = this.configValidator.getDiscoveryConfig().lzyl ?? false;
    const rilinks = this.configValidator.getDiscoveryConfig().rilinks ?? false;

    // First pass: create placements and set up event listeners
    const placements: AdvertorialPlacement[] = [];

    units.forEach((unit) => {
      const trackedUnit = new AdvertorialPlacement(
        unit,
        this.attribution,
        deferUntilContent,
        this.extractor,
        rilinks,
      );
      trackedUnit.setOpportunity(opportunity);
      this.discoveredUnits.set(unit.placementId, trackedUnit);

      trackedUnit.on('AdvertorialImpression', ({ data }) => {
        this.dispatch('AdvertorialImpression', data);
        if (data.deliveryId !== this.attribution.defaultDeliveryId) {
          ActivityManager.getInstance().recordImp(data.deliveryId, data.id);
        }
      });

      trackedUnit.on('AdvertorialInteraction', ({ data }) => {
        this.dispatch('AdvertorialInteraction', data);
        if (data.deliveryId !== this.attribution.defaultDeliveryId) {
          ActivityManager.getInstance().recordInteraction(data.deliveryId);
        }
      });

      trackedUnit.on('ViewableImpression', ({ data }) => {
        this.dispatch('AdvertorialViewableImpression', data);
        if (data.deliveryId !== this.attribution.defaultDeliveryId) {
          ActivityManager.getInstance().recordView(data.deliveryId);
        }
      });

      placements.push(trackedUnit);
    });

    // Dispatch discovery event first to ensure opportunity is recorded before impressions
    this.dispatch('AdvertorialUnitsDiscovered', {
      opportunityId: opportunity.id,
      placements: Array.from(this.discoveredUnits.values()),
    });

    placements.forEach((trackedUnit) => {
      trackedUnit.init();
    });
  }

  /**
   * Read and act on a pending accidental-click flag left by a same-tab redirect.
   * Driven by the `pageshow` listener so it runs on both a full reload and a
   * bfcache restore (back-button), the latter of which runs no discovery.
   *
   * Consume (read-and-delete) is unconditional so a flag is never reconciled
   * twice. Guards against false positives: the click must be within the 1h
   * interaction window and the user must have returned to the same page they
   * left (compared on normalised origin+pathname, so a differing tracking param
   * or fragment doesn't break the match). The original opportunity is gone with
   * the unloaded page, so a fresh one is minted for attribution.
   *
   * The window is the wider INVALID_CLICK_WINDOW_MS (1h) so the analytics
   * layer can fire /iclk. The narrower /aclk decision (BACK_CLICK_WINDOW_MS) is
   * applied downstream from the same awayDuration; this path only widens the
   * dispatch gate and does not change the flag lifecycle (still consumed once on
   * first pageshow).
   */
  private reconcilePendingAccidentalClick(): void {
    const flag = ActivityManager.getInstance().consumePendingAccidentalClick();
    if (!flag) {
      return;
    }

    const awayDuration = Date.now() - flag.ts;
    const withinInteractionWindow =
      awayDuration >= 0 && awayDuration <= INVALID_CLICK_WINDOW_MS;
    const sameUrl =
      normaliseAdvertorialMatchUrl(flag.url) ===
      normaliseAdvertorialMatchUrl(window.location.href);

    if (!withinInteractionWindow || !sameUrl) {
      logger.debug(
        `Discarding stale or mismatched accidental-click flag (withinInteractionWindow: ${withinInteractionWindow}, sameUrl: ${sameUrl})`,
      );
      return;
    }

    logger.info('Reconciling redirect accidental click from previous page load');
    this.dispatch('AdvertorialAccidentalClick', {
      opportunityId: new Opportunity(generateUUID()).id,
      placementId: '',
      id: flag.impressionId,
      deliveryId: flag.deliveryId,
      awayDuration,
      source: 'redirect',
      t: ADVERTORIAL_RECONCILE_T_VALUE,
    });
  }

  /**
   * Stop advertorial tracking and cleanup
   */
  public stop(): void {
    logger.info('Stopping advertorial tracking');

    window.removeEventListener('pageshow', this.pageshowHandler);

    // Stop discovery observer if running
    this.discovery.stop();

    // Clean up observers and timers in all placements before clearing
    this.discoveredUnits.forEach((placement) => {
      placement.destroy();
    });

    this.discoveredUnits.clear();
  }

  /**
   * Get discovered units (for debugging)
   */
  public getDiscoveredUnits(): AdvertorialPlacement[] {
    return Array.from(this.discoveredUnits.values());
  }
}
